import { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listWorkAreas } from '@/lib/api';
import type { WorkArea } from '@/lib/api';

const WORK_AREA_STORAGE_KEY = 'metis-selected-work-area';

interface WorkAreaContextValue {
  selectedWorkAreaId: string | null;
  setSelectedWorkAreaId: (id: string | null) => void;
  workAreas: WorkArea[] | undefined;
  selectedWorkArea: WorkArea | null;
  isLoadingWorkAreas: boolean;
  isErrorWorkAreas: boolean;
  workAreasError: Error | null;
}

const workAreaContext = createContext<WorkAreaContextValue | null>(null);

export function WorkAreaProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [selectedWorkAreaId, setSelectedWorkAreaIdRaw] = useState<string | null>(() => {
    try {
      return localStorage.getItem(WORK_AREA_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  const {
    data,
    isLoading: isLoadingWorkAreas,
    isError: isErrorWorkAreas,
    error: workAreasError,
  } = useQuery({
    queryKey: ['workAreas'],
    queryFn: listWorkAreas,
    refetchInterval: 30_000,
  });

  const workAreas = data?.workAreas;

  const setSelectedWorkAreaId = useCallback((id: string | null): void => {
    setSelectedWorkAreaIdRaw(id);
    try {
      if (id) {
        localStorage.setItem(WORK_AREA_STORAGE_KEY, id);
      } else {
        localStorage.removeItem(WORK_AREA_STORAGE_KEY);
      }
    } catch {
      // Selection persistence is best-effort; in-memory state is already updated.
    }
  }, []);

  const selectedWorkArea = useMemo(
    () => workAreas?.find(workArea => workArea.id === selectedWorkAreaId) ?? null,
    [selectedWorkAreaId, workAreas]
  );

  useEffect(() => {
    if (!workAreas) return;
    if (selectedWorkAreaId && !workAreas.some(workArea => workArea.id === selectedWorkAreaId)) {
      setSelectedWorkAreaId(null);
    }
  }, [selectedWorkAreaId, setSelectedWorkAreaId, workAreas]);

  return (
    <workAreaContext.Provider
      value={{
        selectedWorkAreaId,
        setSelectedWorkAreaId,
        workAreas,
        selectedWorkArea,
        isLoadingWorkAreas,
        isErrorWorkAreas,
        workAreasError,
      }}
    >
      {children}
    </workAreaContext.Provider>
  );
}

export function useWorkArea(): WorkAreaContextValue {
  const ctx = use(workAreaContext);
  if (!ctx) {
    throw new Error('useWorkArea must be used within a WorkAreaProvider');
  }
  return ctx;
}
